/**
 * AdminSignInForm - Admin sign in form with modern Dashboard3 styling
 *
 * Handles admin authentication with email and password.
 * Includes admin privilege check after successful login.
 *
 * @example
 * ```tsx
 * <AdminSignInForm />
 * ```
 */

"use client";

import { Box, Flex, Icon } from "@chakra-ui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { FiAlertCircle } from "react-icons/fi";
import { useRouter } from "next/navigation";
import { getSession, signOut, useSession } from "next-auth/react";
import { Button, Link, Text } from "@/components/ui";
import { LabeledInput } from "@/components/shared/formElements/LabeledInput";
import { useThemeTokens } from "@/lib/theme/tokens";
import { type SignInFormType, signInSchema } from "@/schemas";
import { useAdminSignIn } from "@/hooks/auth";
import { PageTypeAdmin } from "@/lib/types";
import { AdminSignInSuccessView } from "./AdminSignInSuccessView";
import { RoleSelectionModal } from "./RoleSelectionModal";
import { getSwitchableAdminRoles } from "@/lib/utils/roles";
import { useStudioFeatureSettings } from "@/features/dashboard/hooks/useStudioFeatureSettings";
import { useUpdateActiveRole } from "@/hooks/auth/useUpdateActiveRole";
import NextLink from "next/link";

export const AdminSignInForm = () => {
  const theme = useThemeTokens();
  const router = useRouter();
  const { data: session, update: updateSession } = useSession();
  const [isSuccess, setIsSuccess] = useState(false);
  const [showRoleSelection, setShowRoleSelection] = useState(false);
  const [availableRoles, setAvailableRoles] = useState<string[]>([]);
  const [selectedRole, setSelectedRole] = useState<string | null>(null);
  const { updateRole } = useUpdateActiveRole();
  const { choreographerPortalEnabled, isPending: isPortalSettingPending } =
    useStudioFeatureSettings();

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<SignInFormType>({
    resolver: zodResolver(signInSchema),
  });

  const { signIn, isLoading, error, clearError } = useAdminSignIn({
    onSuccess: async () => {
      const session = await getSession();
      const user = session?.user;
      
      if (user) {
        const roles = getSwitchableAdminRoles(user, {
          choreographerPortalEnabled:
            !isPortalSettingPending && choreographerPortalEnabled,
          choreographerPortalKnown: true,
        });
        if (roles.length > 1) {
          setAvailableRoles(roles);
          setShowRoleSelection(true);
        } else {
          setSelectedRole(roles[0] ?? null);
          setIsSuccess(true);
        }
      }
    },
    onError: () => {
      // Error is already set by the hook
    },
  });

  const handleRoleSelect = async (role: string) => {
    try {
      await updateRole(role);
      
      setSelectedRole(role);
      
      setShowRoleSelection(false);
      setIsSuccess(true);
      
      setTimeout(() => {
        window.location.href = PageTypeAdmin.Dashboard;
      }, 1500);
    } catch (error) {
      console.error("Error updating role:", error);
    }
  };

  const handleRoleModalClose = () => {
    signOut({ redirect: false }).then(() => {
      window.location.href = PageTypeAdmin.SignIn;
    });
  };

  const onSubmit = async (data: SignInFormType) => {
    clearError();
    const success = await signIn({
      email: data.email,
      password: data.password,
    });
    
    if (success) {
      reset(); // Clear form on success
    }
  };

  if (showRoleSelection) {
    return (
      <RoleSelectionModal
        isOpen={true}
        availableRoles={availableRoles}
        onRoleSelect={handleRoleSelect}
        onClose={handleRoleModalClose}
      />
    );
  }

  if (isSuccess) {
    return <AdminSignInSuccessView selectedRole={selectedRole} />;
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} style={{ width: "100%" }}>
        <Flex direction="column" gap={4}>
          {error && (
            <Box
              borderRadius="8px"
              borderWidth="1px"
              borderColor={theme.accent.primaryBorder}
              bg={theme.accent.mutedBg}
              color={theme.accent.primary}
              p={4}
              flexDirection="column"
              alignItems="flex-start"
            >
              <Flex align="center" gap={2} w="100%">
                <Box flexShrink={0} h="20px">
                  <Icon as={FiAlertCircle} w="20px" h="20px" color={theme.accent.primary} />
                </Box>
                <Text fontWeight="600" fontSize="sm">
                  {error?.includes("admin privileges") ||
                  error?.includes("admin dashboard") ||
                  error?.includes("Access Denied")
                    ? "Access Denied"
                    : "Login failed"}
                </Text>
              </Flex>
              <Flex align="flex-start" gap={2} w="100%" mt={2}>
                <Box flexShrink={0} w="20px" />
                <Text fontSize="sm">{error}</Text>
              </Flex>
            </Box>
          )}

          <LabeledInput
            name="email"
            label="Email"
            errors={errors}
            register={register}
              type="email"
              placeholder="mail@example.com"
            required
          />

          <LabeledInput
            name="password"
            label="Password"
            errors={errors}
            register={register}
              type="password"
              placeholder="Min. 8 characters"
            required
            />

          <Flex justify="flex-end">
            <Link
              as={NextLink}
              href={PageTypeAdmin.ForgotPassword}
              fontSize="sm"
              color={theme.accent.primary}
              fontWeight="500"
              sx={{
                "@media (hover: hover)": {
                  "&:hover": { textDecoration: "underline" },
                },
              }}
            >
              Forgot password?
            </Link>
          </Flex>

          <Button
            h="44px"
            w="100%"
            type="submit"
            fontSize="sm"
            variant="primary"
            fontWeight="500"
            isLoading={isLoading}
            loadingText="Signing in..."
            disabled={isLoading}
          >
            Sign In
          </Button>
        </Flex>
      </form>
  );
};

